<script setup lang="ts">
/**
 * Platform log viewer (ADR 0041).
 *
 * The reason this page exists: production runs in a tenant whose access
 * policy routes operators through Citrix → Cloud Shell → `az`, so reading
 * a log line used to cost a remote-desktop session. Everything here is
 * reachable through Front Door in an ordinary browser.
 *
 * The filters map 1:1 onto the backend's typed query — the client never
 * sends KQL. `request_id` is the important one: it survives the
 * Container App → Service Bus → Job hop, so clicking one pivots from a
 * failed chat turn to the ingest run behind it.
 */
import { computed, onMounted, ref } from "vue";
import { AdminError, type LogRecord, getLogs } from "../api/admin";
import { useToast } from "../composables/useToast";
import HelpHint from "./HelpHint.vue";
import SkeletonRows from "./SkeletonRows.vue";
import { adminParam } from "../utils/vocabulary";

const toast = useToast();
const props = defineProps<{ devUser: string }>();

const records = ref<LogRecord[]>([]);
const loading = ref(false);
const backend = ref("");
const truncated = ref(false);
const unavailable = ref("");

/**
 * The Audit tab links here as `#/admin?tab=logs&request_id=…` so an
 * operator can go from "who changed this" to "what happened on that
 * request" in one click. Widen the range at the same time — an audit row
 * you're investigating is often older than the default hour.
 */
function requestIdFromHash(): string {
  return adminParam("request_id");
}

const deepLinkedRequestId = requestIdFromHash();

/* Data sources links here as `#/admin?tab=logs&contains=<execution>&
 * level=ERROR&since=-30d` to show the errors from one failed job run. */
const since = ref(adminParam("since") || (deepLinkedRequestId ? "-7d" : "-1h"));
const level = ref(adminParam("level"));
const service = ref(adminParam("service"));
const contains = ref(adminParam("contains"));
const requestId = ref(deepLinkedRequestId);
const docId = ref("");
const limit = ref(200);

const expanded = ref<Set<number>>(new Set());

const RANGES = [
  { label: "15 min", value: "-15m" },
  { label: "1 hour", value: "-1h" },
  { label: "6 hours", value: "-6h" },
  { label: "24 hours", value: "-24h" },
  { label: "7 days", value: "-7d" },
  { label: "30 days", value: "-30d" },
];

const SERVICES = [
  "choir-backend",
  "choir-indexer",
  "choir-kb-indexer",
  "choir-docling-worker",
  "choir-eregnskab-ingest",
  "choir-moondream",
];

const hasFilters = computed(
  () =>
    !!(
      level.value ||
      service.value ||
      contains.value ||
      requestId.value ||
      docId.value
    ),
);

async function reload() {
  loading.value = true;
  unavailable.value = "";
  try {
    const res = await getLogs(
      {
        since: since.value,
        level: level.value || undefined,
        service: service.value || undefined,
        contains: contains.value || undefined,
        requestId: requestId.value || undefined,
        docId: docId.value || undefined,
        limit: limit.value,
      },
      props.devUser,
    );
    records.value = res.records;
    backend.value = res.backend;
    truncated.value = res.truncated;
    expanded.value = new Set();
  } catch (e) {
    // A 503 here means the log backend isn't reachable or isn't
    // configured — surface the message inline rather than as a toast that
    // scrolls away, because it names the fix (usually a missing role).
    const msg = e instanceof AdminError ? e.message : String(e);
    unavailable.value = msg;
    records.value = [];
    toast.error("Could not load logs", msg);
  } finally {
    loading.value = false;
  }
}

onMounted(reload);

/** Filter down to one correlation id — the "what else happened on this
 * request?" pivot, across services and across the ingest queue. */
function pivotToRequest(id: string) {
  if (!id) return;
  requestId.value = id;
  contains.value = "";
  void reload();
}

function clearFilters() {
  level.value = "";
  service.value = "";
  contains.value = "";
  requestId.value = "";
  docId.value = "";
  void reload();
}

function toggle(i: number) {
  if (expanded.value.has(i)) expanded.value.delete(i);
  else expanded.value.add(i);
  expanded.value = new Set(expanded.value);
}

function when(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString();
}

function hasFields(r: LogRecord): boolean {
  return !!r.fields && Object.keys(r.fields).length > 0;
}

/** AdminPage routes on the hash; no router to inject. */
function goToAudit() {
  window.location.hash = "#/admin?tab=audit";
}
</script>

<template>
  <div class="admin-logs">
    <header class="admin-logs__header">
      <!-- Two tabs show "history", and they answer different questions with
           different guarantees — diagnostic logs age out, the audit trail
           does not. The empty state repeats the pointer, because that is the
           moment someone concludes "nothing happened". -->
      <h2>
        Logs
        <HelpHint wide align="start" label="About logs">
          Diagnostic records from every service, kept ~30 days. Looking for
          <em>who changed what</em> — a prompt edit, a share, a delete? That's
          the <button class="link" @click="goToAudit">Audit log</button>, which
          is append-only and retained indefinitely.
        </HelpHint>
      </h2>
      <span v-if="backend" class="muted small">
        source: <code>{{ backend }}</code>
      </span>
    </header>

    <div class="filters">
      <label>
        Range
        <select v-model="since">
          <option v-for="r in RANGES" :key="r.value" :value="r.value">
            {{ r.label }}
          </option>
        </select>
      </label>
      <label>
        Level
        <select v-model="level">
          <option value="">any</option>
          <option>DEBUG</option>
          <option>INFO</option>
          <option>WARNING</option>
          <option>ERROR</option>
          <option>CRITICAL</option>
        </select>
      </label>
      <label>
        Service
        <select v-model="service">
          <option value="">all</option>
          <option v-for="s in SERVICES" :key="s" :value="s">{{ s }}</option>
        </select>
      </label>
      <label>
        Request id
        <input v-model="requestId" placeholder="paste X-Request-Id" />
      </label>
      <label>
        Doc id
        <input v-model="docId" placeholder="kb:12:report-a1b2" />
      </label>
      <label>
        Text
        <input v-model="contains" placeholder="substring in the raw line" />
      </label>
      <label>
        Limit
        <input type="number" min="10" max="1000" v-model.number="limit" />
      </label>
      <button class="btn btn--primary" @click="reload">Refresh</button>
      <button v-if="hasFilters" class="link" @click="clearFilters">
        clear filters
      </button>
    </div>

    <p v-if="unavailable" class="error">{{ unavailable }}</p>

    <p v-if="truncated && !loading" class="muted small">
      Showing the most recent {{ limit }} records — there are more in this
      range. Narrow the filters or shorten the range to see the rest.
    </p>

    <table v-if="!loading && !unavailable" class="admin-table">
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Level</th>
          <th scope="col">Service</th>
          <th scope="col">Message</th>
          <th scope="col">Request</th>
          <th scope="col"><span class="sr-only">Detail</span></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(r, i) in records" :key="i">
          <tr :class="`lvl lvl--${r.level.toLowerCase()}`">
            <td class="mono">{{ when(r.ts) }}</td>
            <td>
              <span class="badge" :class="`badge--${r.level.toLowerCase()}`">{{
                r.level
              }}</span>
            </td>
            <td class="mono small">{{ r.service }}</td>
            <td class="msg">{{ r.msg }}</td>
            <td>
              <button
                v-if="r.request_id"
                class="link mono small"
                :aria-label="`Filter to request ${r.request_id}`"
                @click="pivotToRequest(r.request_id)"
              >
                {{ r.request_id.slice(0, 8) }}
              </button>
              <span v-else class="muted">—</span>
            </td>
            <td>
              <button
                v-if="hasFields(r)"
                class="link"
                :aria-expanded="expanded.has(i)"
                :aria-label="`Structured fields for the ${r.level} at ${when(r.ts)}`"
                @click="toggle(i)"
              >
                {{ expanded.has(i) ? "hide" : "fields" }}
              </button>
            </td>
          </tr>
          <tr v-if="expanded.has(i)" class="log-detail">
            <td colspan="6">
              <pre>{{ JSON.stringify(r.fields, null, 2) }}</pre>
              <p v-if="r.logger || r.revision" class="muted small">
                logger: <code>{{ r.logger || "—" }}</code> · revision:
                <code>{{ r.revision || "—" }}</code>
              </p>
            </td>
          </tr>
        </template>
        <tr v-if="records.length === 0">
          <td colspan="6" class="muted">
            No records match this filter. Logs are kept ~30 days — for who
            changed what, see the
            <button class="link" @click="goToAudit">Audit log</button>.
          </td>
        </tr>
      </tbody>
    </table>
    <SkeletonRows v-else-if="loading" :rows="10" />
  </div>
</template>

<style scoped>
.admin-logs {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-logs__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.msg {
  font-family: var(--font-mono, monospace);
  font-size: 0.85rem;
  word-break: break-word;
}
.badge {
  display: inline-block;
  padding: 0.05rem 0.45rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
}
/* Severity reads at a glance without relying on colour alone — the
   level text is always present in the badge. */
.badge--error,
.badge--critical {
  background: var(--color-error-bg);
  color: var(--color-error);
}
.badge--warning {
  background: var(--color-warn-bg);
  color: var(--color-warn-ink);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.mono {
  font-family: var(--font-mono, monospace);
  white-space: nowrap;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
h2 .link,
td.muted .link {
  font-size: inherit;
}
.log-detail pre {
  white-space: pre-wrap;
  font-family: var(--font-mono, monospace);
  font-size: 0.82rem;
  background: var(--color-surface-alt);
  padding: 0.5rem;
  border-radius: var(--radius-sm);
  margin: 0.25rem 0;
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
}
</style>
